import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Send, Lock, MessageSquare, Building2, Server, ExternalLink, CheckCircle2, X as XIcon, ArrowUpRight, MessageCircle, FileText, History, Download, UserPlus, Sparkles, Copy, Check } from 'lucide-react';
import { aiHelperApi, type AIHelperResponse } from '../api/ai_helper';
import { Markdown } from '../lib/markdown';
import { redmineApi, similarApi, replyTemplatesApi, type SimilarTicket, type ReplyTemplate, type RedmineProject } from '../api/extras';
import { timelineApi } from '../api/v2';
import { kbApi, type KBBrief } from '../api/kb';
import { BookOpen } from 'lucide-react';
import { ticketsApi, type TicketDetail, type TicketStatus, type TicketPriority } from '../api/tickets';
import { authApi } from '../api/auth';
import { api } from '../api/client';
import type { AuthUser } from '../lib/auth';
import { fmtTime, ticketNumber, STATUS_LABEL, STATUS_COLOR, PRIORITY_COLOR, SLA_COLOR, ticketStatusLabel, ticketStatusColor } from '../lib/format';
import { CONTRACT_BADGE, CONTRACT_LABEL, orgsApi, type Installation } from '../api/organizations';
import { Attachments } from '../components/Attachments';
import SecretShareButton from '../components/SecretShareButton';
import TicketAIHelper from '../components/TicketAIHelper';
import TicketMessage from '../components/TicketMessage';
import { useAuth } from '../lib/auth';

const ALL_STATUSES: TicketStatus[] = ['new', 'open', 'pending_customer', 'on_hold', 'resolved', 'closed', 'cancelled'];
const ALL_PRIORITIES: TicketPriority[] = ['P1', 'P2', 'P3', 'P4'];

/** Адрес очереди, с которого пришли (её фильтры). Очередь пишет его в sessionStorage. */
function backToQueueUrl(): string {
  try {
    const saved = sessionStorage.getItem('queue.returnUrl');
    if (saved && saved.startsWith('/')) return saved;
  } catch { /* приватный режим */ }
  return '/';
}

export default function TicketDetailPage() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const isStaff = user?.type === 'staff';
  const [t, setT] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [posting, setPosting] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      setT(await ticketsApi.get(id));
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const send = async () => {
    if (!reply.trim()) return;
    setPosting(true);
    try {
      // author_email/name теперь подставляются на backend из текущего user'а
      await ticketsApi.postMessage(id, { body: reply, is_internal: internal });
      setReply('');
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally {
      setPosting(false);
    }
  };

  const setStatus = async (s: TicketStatus) => {
    if (!t) return;
    setT(await ticketsApi.update(id, { status: s }));
  };

  const setPriority = async (p: TicketPriority) => {
    if (!t) return;
    setT(await ticketsApi.update(id, { priority: p }));
  };

  const setInstallation = async (instId: string | null) => {
    if (!t) return;
    setT(await ticketsApi.update(id, { installation_id: instId }));
  };

  if (loading) return <Loader2 className="animate-spin mx-auto mt-10 text-blue-400" />;
  if (err) return <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded">{err}</div>;
  if (!t) return null;

  return (
    <div>
      {/* Возврат в очередь ровно с теми фильтрами, из которых пришли (адрес запоминает
          сама очередь). Прямой заход по ссылке на тикет — фолбэк на корень. */}
      <Link to={backToQueueUrl()} className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> К списку
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Лента */}
        <div className="space-y-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-1 gap-3">
              <div className="text-xs text-slate-500 font-mono">{ticketNumber(t.number)}</div>
              {isStaff && t.status !== 'closed' && t.status !== 'cancelled' && (
                <div className="flex gap-1.5 flex-wrap">
                  {!t.redmine_issue_id && (
                    <button onClick={() => setShowEscalate(true)}
                      className="text-xs inline-flex items-center gap-1 bg-amber-600 hover:bg-amber-500 text-white font-bold px-3 py-1.5 rounded transition">
                      <ArrowUpRight size={13} /> В L3 (Redmine)
                    </button>
                  )}
                  {t.status !== 'resolved' && (
                    <button onClick={() => setStatus('resolved')}
                      className="text-xs inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-3 py-1.5 rounded transition">
                      <CheckCircle2 size={13} /> Решён
                    </button>
                  )}
                  <button onClick={() => {
                    if (window.confirm(`Закрыть тикет SD-${String(t.number).padStart(4,'0')}?`)) {
                      setStatus('closed');
                    }
                  }}
                    className="text-xs inline-flex items-center gap-1 bg-slate-700 hover:bg-slate-600 text-white font-bold px-3 py-1.5 rounded transition">
                    <XIcon size={13} /> Закрыть
                  </button>
                  <button onClick={async () => {
                    try { await timelineApi.downloadReport(t.id, t.number); }
                    catch (e: any) {
                      alert(e?.response?.data?.detail || e?.message || 'Не удалось скачать отчёт');
                    }
                  }}
                    title="Скачать отчёт-историю (markdown)"
                    className="text-xs inline-flex items-center gap-1 bg-slate-700 hover:bg-slate-600 text-white font-bold px-3 py-1.5 rounded transition">
                    <Download size={13} /> Отчёт
                  </button>
                  <button onClick={() => setShowTimeline(v => !v)}
                    title="Хронология событий"
                    className="text-xs inline-flex items-center gap-1 bg-slate-700 hover:bg-slate-600 text-white font-bold px-3 py-1.5 rounded transition">
                    <History size={13} /> История
                  </button>
                </div>
              )}
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">{t.subject}</h2>

            {/* Клиентские кнопки: возврат в работу / подтверждение закрытия */}
            {!isStaff && (t.status === 'resolved' || t.status === 'closed') && (
              <div className="mb-3 bg-emerald-950/30 border border-emerald-900/60 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="text-sm text-emerald-200 flex-1">
                  {t.status === 'resolved'
                    ? 'Поддержка считает тикет решённым. Подтвердите закрытие или верните в работу. Если не ответите 3 дня — тикет закроется автоматически.'
                    : 'Тикет закрыт. Если проблема осталась — верните в работу одной кнопкой.'}
                </div>
                <div className="flex gap-2 shrink-0">
                  {t.status === 'resolved' && (
                    <button onClick={async () => {
                      try { await ticketsApi.confirmClose(t.id); load(); }
                      catch (e: any) { alert(e?.response?.data?.detail || 'Ошибка'); }
                    }}
                      className="text-xs inline-flex items-center gap-1 bg-emerald-700 hover:bg-emerald-600 text-white font-bold px-3 py-1.5 rounded">
                      <CheckCircle2 size={12} /> Подтверждаю закрытие
                    </button>
                  )}
                  <button onClick={async () => {
                    try { await ticketsApi.reopen(t.id); load(); }
                    catch (e: any) { alert(e?.response?.data?.detail || 'Ошибка'); }
                  }}
                    className="text-xs inline-flex items-center gap-1 bg-amber-700 hover:bg-amber-600 text-white font-bold px-3 py-1.5 rounded">
                    <ArrowUpRight size={12} className="rotate-180" /> Не решено — вернуть в работу
                  </button>
                </div>
              </div>
            )}
            {t.redmine_issue_id && t.redmine_url && (
              <div className="mb-3 inline-flex items-center gap-1.5 bg-amber-900/30 border border-amber-800 text-amber-200 text-xs px-2.5 py-1 rounded">
                <ArrowUpRight size={13} /> Эскалирован в Redmine —{' '}
                <a href={t.redmine_url} target="_blank" rel="noreferrer"
                   className="underline hover:text-white">#{t.redmine_issue_id}</a>
                {isStaff && (
                  <button
                    onClick={async () => {
                      try {
                        const r = await redmineApi.syncNow(t.id);
                        if (r.new_messages) alert(`Подтянуто ${r.new_messages} новых комментариев из Redmine`);
                        else alert('Новых обновлений нет');
                        load();
                      } catch (e: any) {
                        alert('Ошибка синхронизации: ' + (e?.response?.data?.detail || e?.message));
                      }
                    }}
                    title="Подтянуть из Redmine прямо сейчас (без ожидания cron)"
                    className="ml-1 px-1.5 py-0.5 rounded bg-amber-800/50 hover:bg-amber-700/60 text-amber-100 text-[10px] font-bold">
                    ↻ синхр.
                  </button>
                )}
              </div>
            )}
            {/* Markdown-рендер body тикета. Это даёт корректное отображение
                **bold**, ---, списков, переводов строк в тикетах из
                customer-feedback-sync (Gitea → SD), где body содержит структуру
                «**Kind:** …\n**Instance:** …\n---\nописание». Без markdown
                рендера всё сливалось в одну строку. Markdown компонент сам
                делает sanitize + поддерживает таблицы/код. */}
            {t.body && (
              <div className="text-slate-300 text-sm leading-relaxed">
                <Markdown source={t.body} />
              </div>
            )}
            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="text-xs text-slate-500">
                {t.requester_name || t.requester_email || 'Гость'} · создан {fmtTime(t.created_at)}
              </div>
              {isStaff && (
                <BodyAIButton ticketId={t.id} onApply={(text) => setReply(prev => prev ? prev + '\n\n' + text : text)} />
              )}
            </div>
          </div>

          {showTimeline && <TicketTimelinePanel ticketId={t.id} onClose={() => setShowTimeline(false)} />}

          <div className="space-y-3">
            {t.messages.map(m => (
              <TicketMessage key={m.id}
                m={m}
                ticketId={t.id}
                canUseAI={isStaff}
                canToggleVisibility={isStaff}
                onChanged={load}
                redmineUrl={t.redmine_url || null}
                redmineIssueId={t.redmine_issue_id || null}
                onApplyToComposer={(text) => setReply(prev => prev ? prev + '\n\n' + text : text)} />
            ))}
          </div>

          {/* Composer */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3">
            {isStaff && (
              <div className="flex items-center gap-2 flex-wrap">
                <ReplyTemplatesPicker onPick={(body) => setReply(prev => prev ? prev + '\n\n' + body : body)} />
                <KbArticlePicker ticketId={t.id} onSent={load} />
                <SecretShareButton ticketId={t.id} organizationId={t.organization_id || undefined}
                  onLink={(link) => setReply(prev => (prev ? prev + '\n\n' : '') +
                    `🔐 Защищённая ссылка (одноразовая, шифрование на стороне клиента):\n${link}`)} />
              </div>
            )}
            <textarea value={reply} onChange={e => setReply(e.target.value)}
              placeholder={internal ? 'Внутренний комментарий…' : 'Ответить…'}
              rows={4}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none" />
            <div className="flex items-center justify-between">
              {isStaff ? (
                <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                  <input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)} className="accent-yellow-500" />
                  <Lock size={12} /> Внутренний (клиент не увидит)
                </label>
              ) : <span />}
              <button onClick={send} disabled={posting || !reply.trim()}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white text-sm font-bold px-4 py-1.5 rounded">
                {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Отправить
              </button>
            </div>
          </div>
        </div>

        {/* Сайдбар */}
        <aside className="space-y-4">
          <Card title="Вложения">
            <Attachments ownerType="ticket" ownerId={t.id} />
          </Card>

          <Card title="Статус">
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase border ${ticketStatusColor(t)}`}>
              {ticketStatusLabel(t)}
            </span>
            {isStaff && (
              <div className="mt-3 grid grid-cols-2 gap-1">
                {ALL_STATUSES.map(s => (
                  <button key={s} onClick={() => setStatus(s)} disabled={s === t.status}
                    className={`text-[10px] py-1 px-2 rounded uppercase font-bold transition ${
                      s === t.status ? 'bg-blue-700 text-white' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                    }`}>
                    {STATUS_LABEL[s]}
                  </button>
                ))}
              </div>
            )}
          </Card>

          <Card title="Приоритет">
            <div className="flex gap-1">
              {ALL_PRIORITIES.map(p => (
                <button key={p} onClick={() => isStaff && setPriority(p)}
                  disabled={!isStaff}
                  className={`px-3 py-1 rounded text-xs font-bold transition ${
                    p === t.priority ? PRIORITY_COLOR[p] : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                  } ${!isStaff && p !== t.priority ? 'opacity-30 cursor-not-allowed' : ''}`}>
                  {p}
                </button>
              ))}
            </div>
          </Card>

          {t.organization_id && (
            <Card title="Клиент">
              <div className="flex items-start gap-2">
                <Building2 size={14} className="text-blue-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white truncate">{t.organization_name}</div>
                  {t.organization_inn && <div className="text-[11px] font-mono text-slate-500">ИНН {t.organization_inn}</div>}
                </div>
              </div>
              {t.contract_status && (
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] border ${CONTRACT_BADGE[t.contract_status]}`}>
                      {CONTRACT_LABEL[t.contract_status]}
                    </span>
                    {t.contract_days_left != null && t.contract_status !== 'inactive' && (
                      <span className={`text-[11px] font-mono ${
                        t.contract_status === 'expired' ? 'text-red-400' :
                        t.contract_status === 'expiring_soon' ? 'text-yellow-400' :
                        'text-slate-400'
                      }`}>
                        {t.contract_days_left < 0 ? `просрочен на ${-t.contract_days_left} дн` : `осталось ${t.contract_days_left} дн`}
                      </span>
                    )}
                  </div>
                  {t.contract_days_left != null && t.contract_days_left >= 0 && t.contract_status !== 'inactive' && (
                    <div className="h-1 bg-slate-800 rounded overflow-hidden">
                      <div className={`h-full ${
                        t.contract_status === 'expiring_soon' ? 'bg-yellow-500' :
                        t.contract_status === 'expired' ? 'bg-red-500' :
                        'bg-emerald-500'
                      }`} style={{ width: `${Math.min(100, (t.contract_days_left / 365) * 100)}%` }} />
                    </div>
                  )}
                </div>
              )}
              <Link to={`/organizations/${t.organization_id}`}
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white font-bold px-3 py-1.5 rounded transition">
                <Building2 size={12} /> Открыть карточку клиента <ExternalLink size={10} />
              </Link>
            </Card>
          )}

          {t.organization_id && (
            <Card title="Инсталляция">
              <InstallationPicker
                orgId={t.organization_id}
                value={t.installation_id}
                installationName={t.installation_name}
                canEdit={isStaff}
                onChange={setInstallation}
              />
            </Card>
          )}

          {isStaff && (
            <Card title="Линия поддержки">
              <select value={t.support_line ?? ''}
                      onChange={async e => {
                        const v = e.target.value === '' ? null : Number(e.target.value);
                        setT(await ticketsApi.update(id, { support_line: v }));
                      }}
                      className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white focus:border-blue-500 outline-none">
                <option value="">— не назначена —</option>
                <option value="1">1-я линия</option>
                <option value="2">2-я линия</option>
                <option value="3">3-я линия</option>
              </select>
            </Card>
          )}

          {isStaff && (
            <Card title="AI-автопилот">
              <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                <input type="checkbox" checked={t.ai_autopilot_enabled}
                  onChange={async e => {
                    setT(await ticketsApi.update(id, { ai_autopilot_enabled: e.target.checked }));
                  }} />
                {t.ai_autopilot_enabled ? 'Включён' : 'Выключен'}
              </label>
              <p className="text-[10px] text-slate-500 mt-1.5">
                При следующем создании тикета на эту организацию (через триггер)
                или ручном включении здесь AI попытается ответить клиенту первым,
                опираясь на RAG. Если уверенности нет — отдаст агенту.
              </p>
            </Card>
          )}

          <Card title="SLA">
            {(t.sla_first_response_due_at || t.sla_resolution_due_at) ? (
              <SLABadge t={t} />
            ) : (
              <div className="text-xs text-slate-500">SLA-план не привязан к этой организации.</div>
            )}
          </Card>

          {t.rating && (
            <Card title="Оценка работы">
              <div className="text-yellow-400 text-lg font-bold">
                {'★'.repeat(t.rating.score)}<span className="text-slate-700">{'★'.repeat(5 - t.rating.score)}</span>
              </div>
              {t.rating.comment && (
                <p className="text-xs text-slate-300 mt-1.5 italic">«{t.rating.comment}»</p>
              )}
              <div className="text-[10px] text-slate-500 mt-1">
                {t.rating.rated_by_name || 'Клиент'} · {fmtTime(t.rating.created_at)}
              </div>
            </Card>
          )}

          {isStaff && <SimilarTicketsCard ticketId={t.id} />}

          {isStaff && (
            <Card title="AI-помощник">
              <TicketAIHelper
                ticketId={t.id}
                onInsertToCompose={(text) => setReply(prev => prev ? prev + '\n\n' + text : text)}
                onApplyClassify={async (c) => {
                  if (!t) return;
                  const patch: any = {};
                  if (c.priority) patch.priority = c.priority;
                  if (c.support_line) patch.support_line = c.support_line;
                  if (Object.keys(patch).length) {
                    setT(await ticketsApi.update(t.id, patch));
                  }
                }}
              />
            </Card>
          )}

          <Card title="Метаданные">
            <dl className="text-xs space-y-1.5 text-slate-400">
              <div className="flex justify-between"><dt>Создан</dt><dd className="text-slate-300">{fmtTime(t.created_at)}</dd></div>
              <div className="flex justify-between"><dt>Обновлён</dt><dd className="text-slate-300">{fmtTime(t.updated_at)}</dd></div>
              {t.first_responded_at && (
                <div className="flex justify-between"><dt>1-й ответ</dt><dd className="text-green-400">{fmtTime(t.first_responded_at)}</dd></div>
              )}
              {t.resolved_at && (
                <div className="flex justify-between"><dt>Решён</dt><dd className="text-green-400">{fmtTime(t.resolved_at)}</dd></div>
              )}
              <div className="flex items-center justify-between gap-2">
                <dt>Агент</dt>
                <dd className="flex-1 ml-2 min-w-0">
                  <AssigneePicker
                    ticketId={t.id}
                    currentId={t.assignee_id ?? null}
                    currentName={t.assignee_name || t.assignee_email || null}
                    currentUserId={user?.id ?? null}
                    isStaff={isStaff}
                    onChanged={load}
                  />
                </dd>
              </div>
              <div className="flex justify-between"><dt>Сообщений</dt><dd className="text-slate-300 flex items-center gap-1"><MessageSquare size={10} />{t.messages.length}</dd></div>
            </dl>
          </Card>
        </aside>
      </div>

      {showEscalate && t && (
        <EscalateRedmineModal
          ticket={t}
          onClose={() => setShowEscalate(false)}
          onDone={() => { setShowEscalate(false); load(); }}
        />
      )}
    </div>
  );
}


function EscalateRedmineModal({ ticket, onClose, onDone }:
  { ticket: TicketDetail; onClose: () => void; onDone: () => void }) {
  const [members, setMembers] = useState<{id:number; name:string}[]>([]);
  const [assignedTo, setAssignedTo] = useState<number | ''>('');
  const [projects, setProjects] = useState<RedmineProject[]>([]);
  const [projectId, setProjectId] = useState<string>('');   // identifier проекта; '' → дефолт бэкенда
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get('/api/v1/redmine/members')
      .then(r => setMembers(r.data.members || []))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
    redmineApi.projects()
      .then(d => { setProjects(d.projects || []); setProjectId(d.default || ''); })
      .catch(() => setProjects([]));
  }, []);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await redmineApi.escalate(ticket.id, {
        project_id: projectId || undefined,
        note: note.trim() || undefined,
        assigned_to_id: assignedTo || undefined,
      });
      alert(`Создан Redmine issue #${r.redmine_issue_id}\n${r.redmine_url}`);
      onDone();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between items-start">
          <div>
            <h3 className="text-lg font-bold text-white">Эскалация в Redmine (L3)</h3>
            <p className="text-[11px] text-slate-500 mt-0.5 truncate">
              SD-{String(ticket.number).padStart(4,'0')} · {ticket.subject}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><XIcon size={18} /></button>
        </header>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Проект Redmine</span>
            <select value={projectId}
              onChange={e => { setProjectId(e.target.value); setAssignedTo(''); }}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
              {projects.length === 0 && <option value="">по умолчанию</option>}
              {projects.map(p => (
                <option key={p.id} value={p.identifier}>{p.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Назначить на</span>
            <select value={assignedTo}
              onChange={e => setAssignedTo(e.target.value ? Number(e.target.value) : '')}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
              <option value="">— без назначения —</option>
              {loading
                ? <option disabled>загрузка…</option>
                : members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            {!loading && members.length === 0 && (
              <p className="text-[10px] text-slate-500 mt-1">
                Не удалось получить список участников проекта Redmine. Проверь права API-ключа.
              </p>
            )}
          </label>
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">
              Комментарий разработчикам (опционально)
            </span>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={4}
              placeholder="Напр.: «Воспроизводится на 1.8.4. Логи приложил в SD».
Содержание SD-тикета + переписка автоматически попадают в issue."
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
          </label>
          {err && (
            <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">
              {err}
            </div>
          )}
        </div>
        <footer className="p-4 border-t border-slate-800 flex justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded text-sm">Отмена</button>
          <button onClick={submit} disabled={busy}
            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 text-white rounded text-sm font-bold inline-flex items-center gap-1.5">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ArrowUpRight size={13} />}
            Эскалировать
          </button>
        </footer>
      </div>
    </div>
  );
}

function AssigneePicker({
  ticketId, currentId, currentName, currentUserId, isStaff, onChanged,
}: {
  ticketId: string;
  currentId: string | null;
  currentName: string | null;
  currentUserId: string | null;
  isStaff: boolean;
  onChanged: () => Promise<void> | void;
}) {
  const [staff, setStaff] = useState<AuthUser[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!isStaff) return;
    authApi.staff().then(setStaff).catch(() => setStaff([]));
  }, [isStaff]);

  if (!isStaff) {
    return <span className="text-slate-300 truncate block">{currentName || '—'}</span>;
  }

  const change = async (assignee_id: string | null) => {
    if (assignee_id === currentId) return;
    setSaving(true); setErr(null);
    try {
      await ticketsApi.assign(ticketId, { assignee_id });
      await onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setSaving(false); }
  };
  const takeSelf = async () => {
    if (!currentUserId || currentId === currentUserId) return;
    setSaving(true); setErr(null);
    try {
      await ticketsApi.assign(ticketId, { self_assign: true });
      await onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <select
          value={currentId ?? ''}
          onChange={e => change(e.target.value || null)}
          disabled={saving || !staff}
          className="flex-1 bg-slate-900 border border-slate-700 rounded px-1.5 py-0.5 text-xs text-slate-200 min-w-0"
          title="Передать тикет другому агенту"
        >
          <option value="">— не назначен —</option>
          {staff?.map(u => (
            <option key={u.id} value={u.id}>
              {u.full_name || u.email}{u.id === currentUserId ? ' (я)' : ''}
            </option>
          ))}
        </select>
        {currentUserId && currentId !== currentUserId && (
          <button
            onClick={takeSelf}
            disabled={saving}
            title="Взять себе"
            className="shrink-0 inline-flex items-center justify-center w-6 h-6 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white"
          >
            {saving ? <Loader2 size={11} className="animate-spin" /> : <UserPlus size={11} />}
          </button>
        )}
      </div>
      {err && <span className="text-[10px] text-red-400">{err}</span>}
    </div>
  );
}

function InstallationPicker({
  orgId, value, installationName, canEdit, onChange,
}: {
  orgId: string;
  value: string | null;
  installationName: string | null;
  canEdit: boolean;
  onChange: (v: string | null) => Promise<void>;
}) {
  const [installs, setInstalls] = useState<Installation[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    orgsApi.installations(orgId)
      .then(list => { if (alive) setInstalls(list); })
      .catch(() => { if (alive) setInstalls([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orgId]);

  if (!canEdit) {
    if (!value) return <div className="text-xs text-slate-500">не указана</div>;
    return (
      <div>
        <div className="flex items-start gap-2">
          <Server size={14} className="text-blue-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-white truncate">{installationName || value}</div>
          </div>
        </div>
        <Link to={`/organizations/${orgId}?inst=${value}`}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white font-bold px-3 py-1.5 rounded transition">
          <Server size={12} /> Открыть карточку инсталляции <ExternalLink size={10} />
        </Link>
      </div>
    );
  }

  const handle = async (next: string) => {
    setSaving(true); setErr(null);
    try { await onChange(next || null); }
    catch (e: any) { setErr(e?.response?.data?.detail || e?.message || 'Ошибка'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {loading ? (
        <Loader2 size={14} className="animate-spin text-blue-400" />
      ) : !installs || installs.length === 0 ? (
        <div className="text-xs text-slate-500">
          У клиента нет инсталляций.{' '}
          <Link to={`/organizations/${orgId}`} className="text-blue-400 hover:underline">Добавить</Link>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Server size={13} className="text-blue-400 shrink-0" />
          <select value={value || ''} onChange={e => handle(e.target.value)} disabled={saving}
            className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white focus:border-blue-500 outline-none">
            <option value="">— не привязано —</option>
            {installs.map(i => (
              <option key={i.id} value={i.id}>
                {i.name}{i.product ? ` · ${i.product}` : ''}{i.version ? ` ${i.version}` : ''}
              </option>
            ))}
          </select>
          {saving && <Loader2 size={12} className="animate-spin text-blue-400" />}
        </div>
      )}
      {value && (
        <Link to={`/organizations/${orgId}?inst=${value}`}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white font-bold px-3 py-1.5 rounded transition">
          <Server size={12} /> Открыть карточку инсталляции <ExternalLink size={10} />
        </Link>
      )}
      {err && <div className="text-[11px] text-red-400 mt-1">{err}</div>}
    </div>
  );
}

function SLABadge({ t }: { t: TicketDetail }) {
  // Считаем, сколько времени осталось до FRT/TTR (или превышено).
  // Цвета: ok=green, warn=yellow (<33%), at_risk=orange, breach=red.
  const now = Date.now();
  const item = (label: 'FRT' | 'TTR', dueAt: string | null | undefined, doneAt: string | null | undefined,
                createdAt: string) => {
    if (!dueAt) return null;
    const due = new Date(dueAt).getTime();
    const created = new Date(createdAt).getTime();
    const total = Math.max(due - created, 60_000);
    const done = doneAt ? new Date(doneAt).getTime() : null;
    const target = done ?? now;
    const used = Math.max(0, target - created);
    const pct = Math.min(100, (used / total) * 100);
    const overdue = !done && now > due;
    const finished = !!done;
    const remainingMs = due - now;
    const color =
      finished ? 'bg-emerald-500'
      : overdue ? 'bg-red-500'
      : pct >= 80 ? 'bg-orange-500'
      : pct >= 50 ? 'bg-yellow-500'
      : 'bg-emerald-500';
    const text =
      finished ? `выполнен`
      : overdue ? `просрочен на ${humanShort(now - due)}`
      : `осталось ${humanShort(remainingMs)}`;
    return (
      <div className="space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</span>
          <span className={`text-[11px] font-mono ${
            finished ? 'text-emerald-400' : overdue ? 'text-red-400'
              : pct >= 80 ? 'text-orange-300' : 'text-slate-300'
          }`}>{text}</span>
        </div>
        <div className="h-1.5 bg-slate-800 rounded overflow-hidden">
          <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };
  return (
    <div className="space-y-3">
      {item('FRT', t.sla_first_response_due_at, t.first_responded_at, t.created_at)}
      {item('TTR', t.sla_resolution_due_at, t.resolved_at, t.created_at)}
    </div>
  );
}

function humanShort(ms: number): string {
  const m = Math.abs(Math.round(ms / 60_000));
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ч ${m % 60}м`;
  const d = Math.floor(h / 24);
  return `${d} д ${h % 24}ч`;
}


/** Кнопка «AI: ответить» для тела тикета (первое сообщение из MAX/email/web —
 *  оно не Message в БД, а t.body). Под капотом тот же AI-helper, но без
 *  message_id — backend сам подберёт t.body как вопрос. */
function BodyAIButton({ ticketId, onApply }: { ticketId: string; onApply: (text: string) => void }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AIHelperResponse | null>(null);
  const [edited, setEdited] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setLoading(true); setErr(null); setResult(null); setOpen(true);
    try {
      const r = await aiHelperApi.run(ticketId, 'reply', undefined, { refine: true });
      setResult(r); setEdited(r.text);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setLoading(false); }
  };
  const copy = () => {
    const t = edited ?? result?.text ?? '';
    if (!t) return;
    navigator.clipboard.writeText(t).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };

  if (!open) {
    return (
      <button onClick={run}
              title="AI: сформировать ответ на тело тикета (первое сообщение)"
              className="text-[11px] inline-flex items-center gap-1 text-purple-300 hover:text-purple-200 bg-purple-900/20 hover:bg-purple-900/40 border border-purple-900/50 px-2 py-0.5 rounded">
        <Sparkles size={11} /> AI: ответить
      </button>
    );
  }
  return (
    <div className="w-full mt-3 border-t border-slate-800 pt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-purple-300 flex items-center gap-1.5">
          <Sparkles size={11} /> AI-черновик ответа на тело тикета
        </span>
        <button onClick={() => { setOpen(false); setResult(null); setErr(null); }}
                className="text-slate-400 hover:text-white" title="Закрыть">
          <XIcon size={13} />
        </button>
      </div>
      {loading && (
        <div className="flex items-center gap-2 text-xs text-slate-400 py-3">
          <Loader2 size={14} className="animate-spin" />
          AI собирает ответ (2 прохода: черновик → улучшение)…
        </div>
      )}
      {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
      {result && !loading && (
        <>
          <textarea value={edited ?? result.text} onChange={e => setEdited(e.target.value)}
                    rows={Math.min(20, Math.max(6, (edited ?? result.text).split('\n').length + 1))}
                    className="w-full bg-slate-950/60 border border-slate-800 rounded p-3 text-sm text-slate-100 font-sans leading-relaxed focus:outline-none resize-y" />
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <button onClick={() => onApply(edited ?? result.text)}
                    className="text-xs inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white font-bold px-3 py-1.5 rounded">
              <Send size={12} /> Вставить в форму ответа
            </button>
            <button onClick={copy} className="text-xs inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded">
              {copied ? <><Check size={11} /> Скопировано</> : <><Copy size={11} /> Скопировать</>}
            </button>
            <button onClick={run} className="text-xs inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded">
              <Sparkles size={11} /> Перегенерировать
            </button>
          </div>
          {result.sources && result.sources.length > 0 && (
            <div className="mt-2 text-[10px] text-slate-500">
              <span className="mr-1">RAG-источники:</span>
              {result.sources.map((s, i) => (
                <span key={i} className="inline-block bg-slate-800 rounded px-1.5 py-0.5 mr-1 mb-1">
                  [{(s.score * 100 | 0)}%] {s.filename}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">{title}</div>
      {children}
    </div>
  );
}


function TicketTimelinePanel({ ticketId, onClose }: { ticketId: string; onClose: () => void }) {
  const [items, setItems] = useState<any[] | null>(null);
  useEffect(() => {
    timelineApi.get(ticketId).then(setItems).catch(() => setItems([]));
  }, [ticketId]);
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <History size={12} /> Хронология тикета
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-white"><XIcon size={13} /></button>
      </div>
      {!items ? <Loader2 className="animate-spin text-blue-400" size={14} /> :
       items.length === 0 ? <div className="text-xs text-slate-500">События не найдены.</div> :
       <ol className="relative border-l-2 border-slate-800 pl-4 space-y-3">
         {items.map((it, i) => {
           const dot = it.severity === 'critical' ? 'bg-red-500' :
                       it.severity === 'warn' ? 'bg-yellow-500' :
                       it.kind === 'rating' ? 'bg-emerald-500' :
                       it.kind === 'redmine' ? 'bg-amber-500' :
                       it.kind === 'internal' ? 'bg-yellow-700' :
                       'bg-blue-500';
           return (
             <li key={i} className="relative">
               <span className={`absolute -left-[22px] top-1 w-3 h-3 rounded-full ${dot} border-2 border-slate-900`} />
               <div className="text-[10px] text-slate-500 font-mono">{fmtTime(it.at)} {it.actor && `· ${it.actor}`}</div>
               <div className="text-sm text-slate-100">{it.title}</div>
               {it.body && <div className="text-xs text-slate-400 mt-0.5 whitespace-pre-wrap break-words">{it.body}</div>}
             </li>
           );
         })}
       </ol>}
    </div>
  );
}


function ReplyTemplatesPicker({ onPick }: { onPick: (body: string) => void }) {
  const [items, setItems] = useState<ReplyTemplate[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || items.length) return;
    replyTemplatesApi.list().then(setItems).catch(() => {});
  }, [open, items.length]);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="text-[11px] inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-200 px-2 py-1 rounded">
        <FileText size={11} /> Шаблон ответа
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-72 max-h-72 overflow-y-auto bg-slate-900 border border-slate-700 rounded shadow-xl">
          {items.length === 0 ? (
            <div className="p-3 text-xs text-slate-500">Нет шаблонов. Создайте в Настройки → Шаблоны.</div>
          ) : items.map(t => (
            <button key={t.id} type="button"
              onClick={() => { onPick(t.body); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-800 border-b border-slate-800 last:border-b-0">
              <div className="text-sm text-white truncate">{t.title}</div>
              <div className="text-[11px] text-slate-500 truncate">{t.body.slice(0, 100)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function SimilarTicketsCard({ ticketId }: { ticketId: string }) {
  const [items, setItems] = useState<SimilarTicket[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (items !== null || loading) return;
    setLoading(true);
    try { setItems(await similarApi.get(ticketId)); }
    finally { setLoading(false); }
  };
  return (
    <Card title="Похожие тикеты">
      {!open ? (
        <button onClick={() => { setOpen(true); load(); }}
          className="text-xs inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded">
          <MessageCircle size={12} /> Показать похожие
        </button>
      ) : loading ? (
        <Loader2 className="animate-spin text-blue-400 my-2" size={14} />
      ) : items && items.length === 0 ? (
        <div className="text-xs text-slate-500">Похожих не нашлось.</div>
      ) : (
        <ul className="space-y-1.5">
          {(items || []).map(s => (
            <li key={s.id}>
              <Link to={`/tickets/${s.id}`}
                className="block bg-slate-950/60 border border-slate-800 hover:border-slate-600 rounded p-2 transition">
                <div className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="font-mono text-blue-300">SD-{String(s.number).padStart(4, '0')}</span>
                  <span className="text-slate-500">{s.score} совпад.</span>
                </div>
                <div className="text-xs text-white truncate mt-0.5">{s.subject}</div>
                {s.organization_name && (
                  <div className="text-[10px] text-slate-500 truncate">{s.organization_name}</div>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}


function KbArticlePicker({ ticketId, onSent }: { ticketId: string; onSent: () => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [items, setItems] = useState<KBBrief[]>([]);
  const [busy, setBusy] = useState(false);
  const [alsoChannels, setAlsoChannels] = useState(true);
  const [internal, setInternal] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      kbApi.list(q || undefined).then(setItems).catch(() => setItems([]));
    }, 200);
    return () => clearTimeout(t);
  }, [open, q]);

  const send = async (article: KBBrief) => {
    if (!window.confirm(`Отправить статью «${article.title}» в тикет${alsoChannels ? ' и чаты клиента' : ''}?`)) return;
    setBusy(true); setStatus(null);
    try {
      const r = await kbApi.postToTicket(article.id, ticketId, {
        also_channels: alsoChannels, internal,
      });
      const chans: string[] = ['веб-чат тикета'];
      if (r.sent_to_tg) chans.push('Telegram');
      if (r.sent_to_max) chans.push('MAX');
      setStatus(`Отправлено: ${chans.join(' + ')}`);
      setOpen(false);
      onSent();
    } catch (e: any) {
      setStatus(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="text-[11px] inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-200 px-2 py-1 rounded">
        <BookOpen size={11} /> Статья из KB
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-[420px] max-h-96 overflow-y-auto bg-slate-900 border border-slate-700 rounded shadow-xl p-2">
          <input value={q} onChange={e => setQ(e.target.value)} autoFocus
            placeholder="Поиск статьи по заголовку, тексту, тегам…"
            className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white mb-2" />
          <div className="flex items-center gap-3 mb-2 px-1">
            <label className="flex items-center gap-1.5 text-[11px] text-slate-300">
              <input type="checkbox" checked={alsoChannels}
                     onChange={e => setAlsoChannels(e.target.checked)} />
              Также в чаты клиента (TG/MAX)
            </label>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-300">
              <input type="checkbox" checked={internal}
                     onChange={e => setInternal(e.target.checked)} />
              Внутр. комментарий
            </label>
          </div>
          {items.length === 0 ? (
            <div className="text-xs text-slate-500 px-2 py-2">Ничего не найдено</div>
          ) : (
            <ul className="divide-y divide-slate-800">
              {items.slice(0, 30).map(a => (
                <li key={a.id}>
                  <button onClick={() => send(a)} disabled={busy}
                    className="w-full text-left px-2 py-2 hover:bg-slate-800 rounded">
                    <div className="text-sm text-white truncate">{a.title}</div>
                    {a.snippet && (
                      <div className="text-[11px] text-slate-400 line-clamp-2 mt-0.5">{a.snippet}</div>
                    )}
                    {a.tags && <div className="text-[10px] text-slate-600 truncate mt-0.5">{a.tags}</div>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {status && <div className="mt-2 text-[11px] text-slate-300 px-1">{status}</div>}
        </div>
      )}
    </div>
  );
}
